/**
 * 🛣️ Router Configuration
 *
 * Lazy loading للـ pages = أداء أفضل.
 */

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

import MainLayout from '@presentation/layouts/MainLayout';
import { ProtectedRoute } from '@presentation/components/shared/ProtectedRoute';
import { AdminProtectedRoute } from '@presentation/components/shared/AdminProtectedRoute';
import { SuperAdminProtectedRoute } from '@presentation/components/shared/SuperAdminProtectedRoute';
import { SubscriptionGuard } from '@presentation/components/subscription/SubscriptionGuard';
import { ErrorBoundary } from '@presentation/components/shared/ErrorBoundary';

// Lazy loaded
const Login = lazy(() => import('@presentation/pages/Login'));
const SetPasswordFromLink = lazy(
  () => import('@presentation/pages/SetPasswordFromLink')
);
const Dashboard = lazy(() => import('@presentation/pages/Dashboard'));
const Calendar = lazy(() => import('@presentation/pages/Calendar'));
const Venues = lazy(() => import('@presentation/pages/Venues'));
const Packages = lazy(() => import('@presentation/pages/Packages'));
const Bookings = lazy(() => import('@presentation/pages/Bookings'));
const Analytics = lazy(() => import('@presentation/pages/Analytics'));
const AdminDashboard = lazy(() => import('@presentation/pages/AdminDashboard'));
const Settings = lazy(() => import('@presentation/pages/Settings'));
const Reviews = lazy(() => import('@presentation/pages/Reviews'));
const Notifications = lazy(() => import('@presentation/pages/Notifications'));
const VenueDetails = lazy(() => import('@presentation/pages/VenueDetails'));
const AdminPlatformSettings = lazy(() => import('@presentation/pages/AdminPlatformSettings'));
const AdminReviewsModeration = lazy(() => import('@presentation/pages/AdminReviewsModeration'));
const Providers = lazy(() => import('@presentation/pages/Providers'));
const ProviderCreate = lazy(() => import('@presentation/pages/ProviderCreate'));
const ProviderDetails = lazy(() => import('@presentation/pages/ProviderDetails'));
const ForcePasswordChange = lazy(() => import('@presentation/pages/ForcePasswordChange'));
const ProviderTeam = lazy(() => import('@presentation/pages/ProviderTeam'));
const ProviderSelfSettings = lazy(() => import('@presentation/pages/ProviderSelfSettings'));
const Customers = lazy(() => import('@presentation/pages/Customers'));
const CustomerDetails = lazy(() => import('@presentation/pages/CustomerDetails'));
const AdminNotifications = lazy(() => import('@presentation/pages/AdminNotifications'));
const BlockedDates = lazy(() => import('@presentation/pages/BlockedDates'));
const AdminFeaturedVenues = lazy(() => import('@presentation/pages/AdminFeaturedVenues'));
const BulkPricing = lazy(() => import('@presentation/pages/BulkPricing'));
const AdminAnalytics = lazy(() => import('@presentation/pages/AdminAnalytics'));
const Promotions = lazy(() => import('@presentation/pages/Promotions'));
const AdminCities = lazy(() => import('@presentation/pages/AdminCities'));
const Subscription = lazy(() => import('@presentation/pages/Subscription'));
const AdminSubscriptions = lazy(() => import('@presentation/pages/AdminSubscriptions'));
const AdminAuditLog = lazy(() => import('@presentation/pages/AdminAuditLog'));
const AdminCalendarConfig = lazy(() => import('@presentation/pages/AdminCalendarConfig'));

// Page Suspense Loader
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      >
        <Loader2 className="h-8 w-8 text-brand-purple-500" />
      </motion.div>
    </div>
  );
}

/**
 * 🛡️ يلف كل صفحة بـ ErrorBoundary + Suspense
 *   - ErrorBoundary: يلتقط runtime errors ويعرض fallback (بدل شاشة بيضاء)
 *   - Suspense: للـ lazy loading
 */
const withSuspense = (
  Component: React.LazyExoticComponent<React.ComponentType>
) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </ErrorBoundary>
);

export const router = createBrowserRouter([
  {
    path: '/login',
    element: withSuspense(Login),
  },
  // 🔑 (عام) وجهة رابط تعيين/إعادة كلمة السر المُرسل بالإيميل — خارج ProtectedRoute
  {
    path: '/auth/action',
    element: withSuspense(SetPasswordFromLink),
  },
  {
    element: <ProtectedRoute />,
    children: [
      // 🆕 (Phase 4) صفحة تغيير كلمة السر — خارج MainLayout (لا sidebar)
      {
        path: 'change-password',
        element: withSuspense(ForcePasswordChange),
      },
      {
        element: <MainLayout />,
        children: [
          // ═══════════════════════════════════════════════
          // 💎 Subscription page (يفتح حتى لو expired)
          //    لازم تكون خارج SubscriptionGuard
          // ═══════════════════════════════════════════════
          { path: 'subscription', element: withSuspense(Subscription) },

          // ═══════════════════════════════════════════════
          // 🛡️ كل الـ routes الأخرى تحت SubscriptionGuard
          //    provider users الـ expired يحوّلون لـ /subscription
          // ═══════════════════════════════════════════════
          {
            element: <SubscriptionGuard />,
            children: [
              // ═══════════════════════════════════════════════
              // 📂 Public routes (vendor + admin + all roles)
              // ═══════════════════════════════════════════════
              { index: true, element: withSuspense(Dashboard) },
              { path: 'venues', element: withSuspense(Venues) },
              { path: 'venues/:venueId', element: withSuspense(VenueDetails) },
              { path: 'venues/:venueId/packages', element: withSuspense(Packages) },
              { path: 'calendar', element: withSuspense(Calendar) },
              { path: 'bookings', element: withSuspense(Bookings) },
              { path: 'analytics', element: withSuspense(Analytics) },
              { path: 'settings', element: withSuspense(Settings) },
              { path: 'reviews', element: withSuspense(Reviews) },
              { path: 'team', element: withSuspense(ProviderTeam) },
              { path: 'provider-settings', element: withSuspense(ProviderSelfSettings) },
              { path: 'customers', element: withSuspense(Customers) },
              { path: 'customers/:providerId/:customerId', element: withSuspense(CustomerDetails) },
              { path: 'blocked-dates', element: withSuspense(BlockedDates) },
              { path: 'bulk-pricing', element: withSuspense(BulkPricing) },
              { path: 'promotions', element: withSuspense(Promotions) },
              { path: 'notifications', element: withSuspense(Notifications) },

              // ═══════════════════════════════════════════════
              // 🛡️ Admin-only routes (H-2 fix)
              // ═══════════════════════════════════════════════
              {
                element: <AdminProtectedRoute />,
                children: [
                  { path: 'admin', element: withSuspense(AdminDashboard) },
                  { path: 'admin/platform', element: withSuspense(AdminPlatformSettings) },
                  { path: 'admin/reviews', element: withSuspense(AdminReviewsModeration) },
                  { path: 'admin/providers', element: withSuspense(Providers) },
                  { path: 'admin/providers/new', element: withSuspense(ProviderCreate) },
                  { path: 'admin/providers/:providerId', element: withSuspense(ProviderDetails) },
                  { path: 'admin/notifications', element: withSuspense(AdminNotifications) },
                  { path: 'admin/featured-venues', element: withSuspense(AdminFeaturedVenues) },
                  { path: 'admin/analytics', element: withSuspense(AdminAnalytics) },
                  { path: 'admin/cities', element: withSuspense(AdminCities) },
                  { path: 'admin/subscriptions', element: withSuspense(AdminSubscriptions) },
                  {
                    element: <SuperAdminProtectedRoute />,
                    children: [
                      { path: 'admin/audit-log', element: withSuspense(AdminAuditLog) },
                      { path: 'admin/calendar-config', element: withSuspense(AdminCalendarConfig) },
                    ],
                  },
                ],
              },
            ], // SubscriptionGuard.children
          }, // SubscriptionGuard
        ], // MainLayout.children
      }, // MainLayout
    ], // ProtectedRoute.children
  }, // ProtectedRoute
  // 404
  { path: '*', element: <Navigate to="/" replace /> },
]);
